import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DocumentSelect } from "./DocumentSelect";
import { PropertySelect } from "./PropertySelect";
import { FormError } from "@/pages/auth/AuthShell";
import { api, errorMessage } from "@/lib/api";

export const ActionDialog = ({ workspaceId, properties = [], onSaved }) => {
  const EMPTY = { title: "", notes: "", document_id: null, property_id: properties[0]?.id || null };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await api.post(`/workspaces/${workspaceId}/actions`, form);
      setForm(EMPTY); setOpen(false); onSaved();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="action-create-button" className="bg-navy hover:bg-navy-2"><Plus className="mr-1.5 h-4 w-4" /> Add action</Button>
      </DialogTrigger>
      <DialogContent data-testid="action-dialog">
        <DialogHeader><DialogTitle>Add an action</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="a-title">What needs to be done?</Label>
            <Input id="a-title" data-testid="action-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="h-11" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="a-notes">Notes (optional)</Label>
            <Textarea id="a-notes" data-testid="action-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
          </div>
          <div className="space-y-2">
            <Label>Property</Label>
            <PropertySelect properties={properties} value={form.property_id} onChange={(v) => setForm({ ...form, property_id: v })} testId="action-property-select" />
          </div>
          <div className="space-y-2">
            <Label>Linked document (optional)</Label>
            <DocumentSelect workspaceId={workspaceId} value={form.document_id} onChange={(v) => setForm({ ...form, document_id: v })} testId="action-document-select" />
          </div>
          <FormError message={error} testId="action-form-error" />
          <Button type="submit" disabled={busy} data-testid="action-save-button" className="h-11 w-full bg-navy hover:bg-navy-2">{busy ? "Saving..." : "Save action"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
